/** @typedef {import('headlight-configurable-application').Pict} Pict */

const libPictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const libMarked = require('marked');

/**
 * CustomInputHandler class.
 *
 * @class
 * @extends libPictSectionInputExtension
 * @memberof providers.inputs
 */
class CustomInputHandler extends libPictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('pict')} */
		this.pict;
		/** @type {import('pict')} */
		this.fable;
		/** @type {any} */
		this.log;
	}

	/**
	 * Generates the HTML ID for a content display input element.
	 *
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the content display input element.
	 */
	getContentDisplayHTMLID(pInputHTMLID)
	{
		return `#DISPLAY-FOR-${pInputHTMLID}`;
	}

	/**
	 * Generates a tabular content display input ID based on the provided input HTML ID and row index.
	 *
	 * @param {string} pInputHTMLID - The input HTML ID.
	 * @param {number} pRowIndex - The row index.
	 * @return {string} - The generated tabular content display input ID.
	 */
	getTabularContentDisplayInputID(pInputHTMLID, pRowIndex)
	{
		return `#DISPLAY-FOR-TABULAR-${pInputHTMLID}-${pRowIndex}`;
	}

	getInputContent(pInput, pValue)
	{
		let tmpContent = null;
		if (pValue && (typeof(pValue) === 'string'))
		{
			tmpContent = pValue;
		}
		if (pInput.Content && (typeof(pInput.Content) === 'string'))
		{
			tmpContent = pInput.Content;
		}
		if (pInput.Default && (typeof(pInput.Default) === 'string'))
		{
			tmpContent = pInput.Default;
		}

		if (tmpContent)
		{
			tmpContent = libMarked.parse(tmpContent);
		}
		return tmpContent;
	}

	/**
	 * Adds Bulma flavor to the provided content (expected "default Bulma classes" on HTML elements).
	 * @param {string} pContent - The content to which to add Bulma flavor.
	 * @return {string} - The content with Bulma flavor added.
	 */
	addBulmaFlavor(pContent)
	{
		if (pContent)
		{
			// for tables, add the table class
			pContent = pContent.replace(/<table>/g, '<table class="table is-fullwidth is-striped is-hoverable">');
			// for images, add the image class
			pContent = pContent.replace(/<img/g, '<img class="image"');
			// h1-h6 (headings), add the title class with appropriate size modifier (NOTE: we bump down the size by 1 to make it less prominent)
			pContent = pContent.replace(/<h1>/g, '<h1 class="title is-2">');
			pContent = pContent.replace(/<h2>/g, '<h2 class="title is-3">');
			pContent = pContent.replace(/<h3>/g, '<h3 class="title is-4">');
			pContent = pContent.replace(/<h4>/g, '<h4 class="title is-5">');
			pContent = pContent.replace(/<h5>/g, '<h5 class="title is-6">');
			pContent = pContent.replace(/<h6>/g, '<h6 class="title is-7">');
		}
		return pContent;
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpContent = this.getInputContent(pInput, pValue);
		const tmpBulmaFlavoredContent = this.addBulmaFlavor(tmpContent);
		if (tmpBulmaFlavoredContent)
		{
			this.pict.ContentAssignment.assignContent(this.getContentDisplayHTMLID(pInput.Macro.RawHTMLID), tmpBulmaFlavoredContent);
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, tmpBulmaFlavoredContent, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpContent = this.getInputContent(pInput, pValue);
		const tmpBulmaFlavoredContent = this.addBulmaFlavor(tmpContent);
		if (tmpBulmaFlavoredContent)
		{
			this.pict.ContentAssignment.assignContent(this.getTabularContentDisplayInputID(pInput.Macro.RawHTMLID, pRowIndex), tmpBulmaFlavoredContent);
		}
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpContent = this.getInputContent(pInput, pValue);
		const tmpBulmaFlavoredContent = this.addBulmaFlavor(tmpContent);
		if (tmpBulmaFlavoredContent)
		{
			this.pict.ContentAssignment.assignContent(this.getContentDisplayHTMLID(pInput.Macro.RawHTMLID), tmpBulmaFlavoredContent);
		}
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to a form in tabular format.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value parameter.
	 * @param {string} pHTMLSelector - The HTML selector parameter.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the data marshaling.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpContent = this.getInputContent(pInput, pValue);
		const tmpBulmaFlavoredContent = this.addBulmaFlavor(tmpContent);
		if (tmpBulmaFlavoredContent)
		{
			this.pict.ContentAssignment.assignContent(this.getTabularContentDisplayInputID(pInput.Macro.RawHTMLID, pRowIndex), tmpBulmaFlavoredContent);
		}
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
